/**
 * RichTextEditor — WYSIWYG editor (ReactQuill) with a **markdown** I/O contract.
 *
 * The editor works in HTML internally (Quill), but every value crossing the
 * component boundary is markdown: `initialValue` / `setContent` take markdown
 * (seeded via `marked`), and `getContent` / `onChange` emit markdown (via
 * `turndown`). This keeps everything the app sends/stores in markdown — the
 * same format the chat pipeline and message renderer use.
 *
 * Toolbar: bold, italic, underline, strike, lists, blockquote, link, inline
 * code, code block. `showToolbar` toggles its visibility. `onEnterSubmit` makes
 * Enter submit (Shift+Enter inserts a newline) for chat-style usage.
 *
 * @example
 * ```tsx
 * const ref = useRef<RichTextEditorRef>(null);
 * <RichTextEditor ref={ref} initialValue="**hi**" onChange={(md) => ...} />
 * const md = ref.current?.getContent(); // markdown
 * ```
 */

import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
  useEffect,
} from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { marked } from 'marked';
import TurndownService from 'turndown';
import { cn } from '../../lib/utils';

// ── markdown ⇄ html converters (module singletons) ──
const turndown = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  bulletListMarker: '-',
});
// Quill emits <s> for strikethrough — GFM markdown is ~~text~~.
turndown.addRule('strikethrough', {
  filter: ['del', 's'] as unknown as TurndownService.Filter,
  replacement: (content) => `~~${content}~~`,
});
// Underline has no markdown form; keep it as raw <u> (our renderer allows it).
turndown.keep(['u']);

const mdToHtml = (md: string): string => {
  if (!md) return '';
  try {
    return marked.parse(md, { async: false }) as string;
  } catch {
    return md;
  }
};

const htmlToMarkdown = (html: string): string => {
  if (!html) return '';
  // Quill's empty document is "<p><br></p>".
  if (/^\s*<p>\s*(<br\s*\/?>)?\s*<\/p>\s*$/i.test(html)) return '';
  try {
    return turndown.turndown(html).trim();
  } catch {
    return '';
  }
};

/** Full toolbar / formats — the default (used by the startup profile). */
const DEFAULT_TOOLBAR = [
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'bullet' }, { list: 'ordered' }],
  ['blockquote', 'code-block'],
  ['link', 'code'],
];
const DEFAULT_FORMATS = [
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'blockquote',
  'code-block',
  'link',
  'code',
];

export interface RichTextEditorProps {
  /** Initial content as **markdown**. */
  initialValue?: string;
  placeholder?: string;
  /** Fired with **markdown** on every change. */
  onChange?: (markdown: string) => void;
  className?: string;
  style?: React.CSSProperties;
  /** Show the formatting toolbar (default: true). */
  showToolbar?: boolean;
  /** When set, Enter calls this (Shift+Enter inserts a newline). */
  onEnterSubmit?: () => void;
  /**
   * Quill toolbar config. Defaults to the full set; pass a narrower array to
   * restrict formatting, or `false` for no toolbar at all (the chat box hides
   * it and relies on markdown-shortcut typing instead). Captured once on mount
   * to avoid re-initialising Quill.
   */
  toolbar?: unknown[] | false;
  /** Quill enabled formats — keep in sync with `toolbar`. */
  formats?: string[];
  /**
   * Text direction of the editable area. Applied directly to Quill's root
   * (`.ql-editor`) — a `dir` on a wrapper doesn't reliably reach the
   * contenteditable, and `.ql-editor` forces `text-align: left` by default.
   */
  dir?: 'ltr' | 'rtl';
}

export interface RichTextEditorRef {
  /** Current content as **markdown**. */
  getContent: () => string;
  /** Replace content; input is **markdown**. */
  setContent: (markdown: string) => void;
  /**
   * Voice dictation. `finalText` (a newly finalized phrase) is committed
   * permanently at the end; `interimText` (the in-progress guess) is shown as a
   * replaceable tail after it. Each call removes the previous interim tail first,
   * so words appear live without resetting the caret or clobbering manual edits.
   * The caret is left at the end.
   */
  dictate: (finalText: string, interimText: string) => void;
  /** Stop tracking the interim tail (call when dictation ends). Text is kept. */
  endDictation: () => void;
  focus: () => void;
  clear: () => void;
}

const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(
  (
    {
      initialValue = '',
      placeholder = 'Start typing...',
      onChange,
      className = '',
      style,
      showToolbar = true,
      onEnterSubmit,
      toolbar = DEFAULT_TOOLBAR,
      formats = DEFAULT_FORMATS,
      dir = 'ltr',
    },
    ref
  ) => {
    const quillRef = useRef<ReactQuill>(null);
    // Quill owns HTML internally; we seed once from markdown and re-seed only
    // when the incoming markdown genuinely differs (avoids fighting typing).
    const [html, setHtml] = useState<string>(() => mdToHtml(initialValue));
    // Length (chars) of the interim dictation tail currently shown at the end,
    // so the next `dictate` call can remove it before showing the new guess.
    const interimLenRef = useRef(0);

    useEffect(() => {
      if (initialValue !== htmlToMarkdown(html)) {
        setHtml(mdToHtml(initialValue));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValue]);

    useImperativeHandle(ref, () => ({
      getContent: () => htmlToMarkdown(quillRef.current?.getEditor().root.innerHTML ?? html),
      setContent: (markdown: string) => {
        interimLenRef.current = 0;
        setHtml(mdToHtml(markdown));
      },
      dictate: (finalText: string, interimText: string) => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        // Append `str` at the true end (Quill's length counts a trailing '\n'),
        // with a separating space when needed. Returns the inserted length.
        const appendAtEnd = (str: string): number => {
          const idx = Math.max(0, editor.getLength() - 1);
          const existing = editor.getText(0, idx);
          const needsSpace = existing.trim().length > 0 && !/\s$/.test(existing);
          const ins = (needsSpace ? ' ' : '') + str;
          editor.insertText(idx, ins, 'user');
          return ins.length;
        };
        // 1. Remove the previously shown interim tail.
        if (interimLenRef.current > 0) {
          const end = Math.max(0, editor.getLength() - 1);
          const from = Math.max(0, end - interimLenRef.current);
          editor.deleteText(from, end - from, 'user');
          interimLenRef.current = 0;
        }
        // 2. Commit the finalized phrase permanently, then 3. show new interim.
        if (finalText) appendAtEnd(finalText);
        if (interimText) interimLenRef.current = appendAtEnd(interimText);
        editor.setSelection(editor.getLength() - 1, 0);
      },
      endDictation: () => {
        interimLenRef.current = 0;
      },
      focus: () => quillRef.current?.focus(),
      clear: () => {
        interimLenRef.current = 0;
        setHtml('');
      },
    }));

    // Enter-to-submit is read through a ref so the Quill modules stay stable
    // (changing `modules` re-inits Quill and drops content).
    const enterSubmitRef = useRef(onEnterSubmit);
    enterSubmitRef.current = onEnterSubmit;

    // Capture toolbar/formats once — Quill re-inits (dropping content) if the
    // modules object changes identity, so we never recompute them.
    const toolbarRef = useRef(toolbar);
    const formatsRef = useRef(formats);

    const modules = useMemo(
      () => ({
        toolbar: toolbarRef.current,
        keyboard: {
          bindings: {
            submitOnEnter: {
              key: 'Enter',
              shiftKey: false,
              handler: () => {
                if (enterSubmitRef.current) {
                  enterSubmitRef.current();
                  return false; // handled — don't insert a newline
                }
                return true; // no submit handler → default newline
              },
            },
          },
        },
      }),
      []
    );

    const handleChange = (nextHtml: string) => {
      setHtml(nextHtml);
      onChange?.(htmlToMarkdown(nextHtml));
    };

    // Apply direction straight to Quill's editable root so Arabic input flows
    // RTL and right-aligns. `html` is a dep so it re-applies after re-seeds.
    useEffect(() => {
      const root = quillRef.current?.getEditor().root;
      if (!root) return;
      root.setAttribute('dir', dir);
      root.style.textAlign = dir === 'rtl' ? 'right' : 'left';
    }, [dir, html]);

    return (
      <div
        className={cn(
          'rich-text-editor-wrapper',
          // Toggle the Quill toolbar without re-initialising the editor.
          !showToolbar && '[&_.ql-toolbar]:hidden [&_.ql-container]:!border-t',
          className
        )}
        style={style}
      >
        <div className="quill-editor-container">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={html}
            onChange={handleChange}
            placeholder={placeholder}
            modules={modules}
            formats={formatsRef.current}
          />
        </div>
      </div>
    );
  }
);

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
